import { toast } from "sonner";
import { captureError } from "./analytics";

/**
 * Standardized user-facing error messages for key flows.
 * Replaces silent catch blocks with clear branded notifications.
 *
 * Usage:
 *   catch (err) { showApiError(err, "save your drawing") }
 *   catch (err) { showApiError(err) } // generic
 */

const NETWORK_MSG = "Connection issue. Please check your internet and retry.";
const TIMEOUT_MSG = "The request took too long. Please try again.";

/**
 * Normalise FastAPI / Pydantic error payloads into a single string suitable for
 * display in JSX. FastAPI returns validation failures as
 *   { detail: [{ type, loc, msg, input, ctx }, ...] }
 * Rendering that array directly crashes React ("Objects are not valid as a
 * React child"). Use this helper wherever you'd otherwise do
 *   err.response?.data?.detail || "fallback"
 */
export function formatApiDetail(detail, fallback = "Something went wrong. Please try again.") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // Pydantic v2 validation errors. Prefer the human-readable `msg`.
    const msgs = detail
      .map((d) => {
        if (!d) return null;
        if (typeof d === "string") return d;
        if (typeof d.msg === "string") {
          const field = Array.isArray(d.loc) && d.loc.length > 1 ? d.loc[d.loc.length - 1] : null;
          return field ? `${field}: ${d.msg}` : d.msg;
        }
        return null;
      })
      .filter(Boolean);
    return msgs.length ? msgs.join(" · ") : fallback;
  }
  if (typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.message === "string") return detail.message;
    try { return JSON.stringify(detail); } catch { return fallback; }
  }
  return String(detail);
}

export function showApiError(error, action = "") {
  // Canceled/aborted requests are NOT failures — a component unmounted
  // mid-request, a superseded fetch, or a navigation cancel. These must never
  // toast or trip the "Reconnecting" banner.
  if (
    error?.code === "ERR_CANCELED" ||
    error?.name === "CanceledError" ||
    error?.name === "AbortError" ||
    error?.message === "canceled"
  ) {
    return;
  }

  // When offline, the persistent OfflineBanner handles user messaging.
  // Suppress individual toasts to avoid toast spam.
  if (!navigator.onLine) {
    console.error(`[API Error, Offline]${action ? ` (${action})` : ""}:`, error);
    return;
  }

  // Backend briefly unreachable (no response / timeout / 5xx) while the browser
  // IS online — this is the Cloudflare/cold-start case. Surface the friendly
  // "Reconnecting…" banner (which auto-recovers + offers Try again) instead of
  // a scary red toast that makes the whole site feel broken.
  const status = error?.response?.status;
  const isServerDown = !error?.response
    || error?.code === "ECONNABORTED"
    || (typeof status === "number" && status >= 500);
  if (isServerDown) {
    try { window.dispatchEvent(new CustomEvent("iw-server-unreachable")); } catch { /* noop */ }
    captureError(error, { action, message: "server-unreachable" });
    console.error(`[API Error, Server unreachable]${action ? ` (${action})` : ""}:`, error);
    return;
  }

  let message;

  if (error?.code === "ECONNABORTED" || error?.message?.includes("timeout")) {
    message = TIMEOUT_MSG;
  } else if (error?.response?.status === 401) {
    message = "Your session has expired. Please log in again.";
  } else if (error?.response?.status === 403) {
    message = "You don't have permission to do that.";
  } else if (error?.response?.status === 429) {
    message = "Too many requests. Please wait a moment and try again.";
  } else if (error?.response?.status >= 500) {
    message = action
      ? `Something went wrong while trying to ${action}. Please try again.`
      : "Something went wrong on our end. Please try again.";
  } else if (error?.response?.data?.detail) {
    message = formatApiDetail(error.response.data.detail,
      action ? `We couldn't ${action}. Please try again.` : "Something went wrong. Please try again.");
  } else if (error?.response?.data?.error) {
    message = typeof error.response.data.error === "string"
      ? error.response.data.error
      : formatApiDetail(error.response.data.error);
  } else {
    message = action
      ? `We couldn't ${action}. Please try again.`
      : "Something went wrong. Please try again.";
  }

  // Report to error monitoring
  captureError(error, { action, message });

  toast.error(message);
  console.error(`[API Error]${action ? ` (${action})` : ""}:`, error);
}
